import { useBoolean } from '@fluentui/react-hooks';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import jBrochure from '../../../assets/icons/json/documents/brochure-red.json';
import jContrat from '../../../assets/icons/json/documents/contracter-red.json';
import jCourrier from '../../../assets/icons/json/documents/courrier-red.json';
import jDossier from '../../../assets/icons/json/documents/dossier-red.json';
import jPlan from '../../../assets/icons/json/documents/plan-red.json';
import jRecu from '../../../assets/icons/json/documents/recu-red.json';
import jTrombone from '../../../assets/icons/json/documents/trombone-red.json';
import { useDocuments, useDocumentsManager } from '../../../contexts/documentContext';
import Banner from '../../Banner';
import BannerSmall from '../../BannerSmall';
import Page from '../../Page';
import TableDocuments from './TableDocuments';
import Viewer from './Viewer';

const Documents = () => {

    const { documents } = useDocuments();
    const { getFileData, markAsRead } = useDocumentsManager();
    const { state } = useLocation();

    useEffect(() => {
        setSearchedValue(undefined);
        setType(undefined);
    }, [state]);

    const [openedDocument, setOpenedDocument] = useState({});
    const [isViewerOpen, { setTrue: showViewer, setFalse: hideViewer }] = useBoolean(false);
    const [searchedValue, setSearchedValue] = useState(undefined);
    const [type, setType] = useState(undefined);

    const activeDocuments = useMemo(() => {
        if (!type && !searchedValue) return [];
        else {
            let result = documents;
            if (!!type) result = result.filter(document => document.type === type);
            if (!!searchedValue) result = result.filter(document => {
                const { typeDocument, creerLe, nom } = document;

                // https://stackoverflow.com/questions/5700636/using-javascript-to-perform-text-matches-with-without-accented-characters
                const rawValue = searchedValue.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
                const rawTypeDocument = typeDocument.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
                const rawCreerLe = creerLe.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
                const rawNom = nom.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();

                return rawTypeDocument.includes(rawValue)
                    || rawCreerLe.includes(rawValue)
                    || rawNom.includes(rawValue);
            });
            return result;
        }
    }, [documents, searchedValue, type]);

    const activeTypes = useMemo(() => {
        if (!!type) return [type];
        else {
            const types = activeDocuments.map(document => document.type); // Types des documents retournés par la recherche
            if (!!searchedValue) types.push("search"); // Force l'affichage réduit lors d'une recherche
            const uniques = [...new Set(types)]; // Suppression des doublons
            return uniques;
        }
    }, [activeDocuments, searchedValue, type]);

    const categories = useMemo(() => {
        const categories = [
            { json: jPlan, key: "plans", label: "Plans" },
            { json: jContrat, key: "contratEtActe", label: "Contrat & Acte" },
            { json: jDossier, key: "documentsAdministratifs", label: "Documents Administratifs" },
            { json: jCourrier, key: "courriers", label: "Courriers" },
            { json: jRecu, key: "personnalisationDuLogement", label: "Personnalisation du logement" },
            { json: jTrombone, key: "piecesJointes", label: "Pièces jointes" },
            { json: jBrochure, key: "documentsCommerciaux", label: "Documents Commerciaux" }
        ];
        return categories.map(category => ({
            ...category,
            badgeLabel: documents.filter(document => document.type === category.key && !document.lu).length
        }));
    }, [documents]);

    const onDismissed = useCallback(() => {
        setOpenedDocument({});
    }, []);

    const onPreview = async (document) => {
        let { fileData } = document;
        if (!fileData) {
            fileData = await getFileData(document);
            markAsRead(document);
            document.fileData = fileData;
        }
        setOpenedDocument(document);
        showViewer();
    };

    const onSearch = useCallback((newValue) => {
        setSearchedValue(newValue);
    }, []);

    return <Page title="Mes documents" onSearch={onSearch} searchedValue={searchedValue}>

        {!!type || !!searchedValue // Afficher une petite bannière si l'utilisateur sélectionne un type de document ou effectue une recherche
            ? <BannerSmall categories={categories} onChange={type => setType(type)} types={activeTypes} />
            : <Banner categories={categories} onChange={type => setType(type)} types={activeTypes} />
        }
        {(!!type || !!searchedValue) && <TableDocuments documents={activeDocuments} onPreview={onPreview} />}

        <Viewer
            document={openedDocument}
            isOpen={isViewerOpen}
            onDismiss={hideViewer}
            onDismissed={onDismissed}
        />

    </Page>;

};

export default Documents;